import { useCallback, useEffect, useMemo } from 'react'
import { Button, Tooltip } from '@highlight/ui'
import { QueuedMessagesPanel } from '@renderer/chat'
import { MessageList } from '@renderer/chat/components'
import { MessageCircle, SquareArrowOutUpRight } from 'lucide-react'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useUpdateChatTitle } from '@renderer/hooks/chat-history'
import { useViewContext, useViewEntranceSettled } from '../../view-context'
import { View, useViewBodyBottomChrome, useViewBodyBottomInset } from '../../components/view'
import { pendingAnswerableGlowTint } from '../action-view/action-view-state'
import { useSetFloatingBarScrollSlot } from '../../atoms'
import { useUpdateTopViewContext } from '../../view-atoms'
import { ChatShareControl } from '../../components/chat/chat-share-control'
import { ChatMessageListSkeleton } from '../../components/chat/chat-message-list-skeleton'
import { EditableTitle } from '../../components/editable-title'
import { ChatSuggestions } from './nested/chat-suggestions'
import { SkillCompareBar } from '../../components/skill-compare-bar'
import { useChatViewModel, type ChatViewContext, type ChatViewModel } from './use-chat-view-model'
import { useConversationPendingAnswerables } from './use-conversation-pending-answerables'
import { openViewWindow } from '../../open-view-window'
import { canPopOutView } from '../../surface'

const SHARE_EVENT_PROPS = { source: 'chat-view' }

/**
 * Chat View: the conversation surface as a native View. Re-skin only — the
 * chat UI itself (message list, streaming, queued messages) is untouched;
 * this file owns the view chrome and `use-chat-view-model` owns the behavior.
 */
export function ChatView() {
  const { context } = useViewContext<ChatViewContext>()
  const updateChatContext = useUpdateTopViewContext('chat')
  // Small cached chats mount immediately. Large transcripts still wait for the
  // slide-in to settle so markdown rendering does not drop those frames.
  const entranceSettled = useViewEntranceSettled()
  const model = useChatViewModel(context, entranceSettled)
  const { activeChatId, showScrollToEnd, scrollToEnd } = model

  // `fade` rather than `list`: a message is prose, and the last line washing
  // out mid-word reads as a bug. Memoized because `MessageList` compares
  // props shallowly.
  const paddingBottom = useViewBodyBottomInset()
  const messageListStyle = useMemo(() => ({ paddingBottom }), [paddingBottom])
  const bottomChrome = useViewBodyBottomChrome()

  // Jump-to-latest lives in the shared floating bar row, like every other view.
  const setScrollSlot = useSetFloatingBarScrollSlot()
  useEffect(() => {
    setScrollSlot({ show: showScrollToEnd, onClick: () => scrollToEnd() })
    return () => setScrollSlot(null)
  }, [showScrollToEnd, scrollToEnd, setScrollSlot])

  // Mirror "a question is waiting on the user" into the view context so the
  // registry can gate the floating question card (see view-registry) — the
  // hoisted answer surface for the deferring cards in the thread. That card's
  // action also tints the view's glow.
  const pendingAnswerable = useConversationPendingAnswerables(activeChatId)[0]
  const hasPendingQuestions = !!pendingAnswerable
  useEffect(() => {
    updateChatContext((prev) =>
      prev && (prev.hasPendingQuestions ?? false) !== hasPendingQuestions ? { ...prev, hasPendingQuestions } : prev,
    )
  }, [hasPendingQuestions, updateChatContext])

  return (
    <View bottomFadeTint={pendingAnswerable && pendingAnswerableGlowTint(pendingAnswerable)}>
      <ChatHeader model={model} />
      <SkillCompareBar
        chatId={model.activeChatId}
        // The view's own claim and reconciliation follow the new id.
        onSwitchSide={(chatId) =>
          updateChatContext((prev) => (prev ? { ...prev, chatId, isNew: false, questionActionId: undefined } : prev))
        }
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 flex-col overflow-hidden">
          {model.showSuggestions ? (
            <ChatSuggestions onSuggestionClick={model.onSuggestionClick} />
          ) : !model.isLoadingMessages ? (
            <MessageList
              className="flex-1"
              style={messageListStyle}
              bottomChrome={bottomChrome}
              onStagedActionClick={model.onStagedActionClick}
              onStagedActionCreated={model.onStagedActionCreated}
              onReminderClick={model.onReminderClick}
            />
          ) : (
            <ChatMessageListSkeleton />
          )}
        </div>
        <QueuedMessagesPanel chatId={model.activeChatId} />
      </div>
    </View>
  )
}

function ChatHeader({ model }: { model: ChatViewModel }) {
  const { mutateAsync: updateChatTitle } = useUpdateChatTitle()
  const updateChatContext = useUpdateTopViewContext('chat')
  const { activeChatId, shareChatId, chatTitle } = model

  // Undefined until the backend has assigned the chat an id: a new chat has nothing to rename yet.
  const handleSaveTitle = useMemo(() => {
    if (!shareChatId) {
      return undefined
    }
    return async (title: string) => {
      await updateChatTitle({ id: shareChatId, title })
      updateChatContext((prev) => (prev ? { ...prev, title } : prev))
      AmplitudeRenderer.trackEvent('Sidepanel Header', { action: 'rename', surface: 'chat' })
    }
  }, [shareChatId, updateChatTitle, updateChatContext])

  const handleOpenInNewWindow = useCallback(() => {
    AmplitudeRenderer.trackEvent('Sidepanel Header', {
      action: 'open in new window',
      surface: 'chat',
      isNewChat: !activeChatId,
    })
    openViewWindow({ id: 'chat', context: activeChatId ? { chatId: activeChatId } : { isNew: true } })
  }, [activeChatId])

  return (
    <div className="shrink-0 p-3">
      <div className="flex items-center justify-between gap-0.5">
        <div className="group flex min-w-0 flex-1 items-center gap-2 ps-1">
          <MessageCircle className="text-foreground size-4 shrink-0" aria-hidden="true" />
          <EditableTitle title={chatTitle} onSave={handleSaveTitle} showRenameButton={false} />
        </div>

        <ChatShareControl chatId={shareChatId} eventProps={SHARE_EVENT_PROPS} title={chatTitle} />
        {canPopOutView && (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button variant="ghost" shape="square" aria-label="Open in new window" onClick={handleOpenInNewWindow}>
                  <SquareArrowOutUpRight />
                </Button>
              }
            />
            <Tooltip.Content side="bottom">Open in new window</Tooltip.Content>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
